import React, { useState, useCallback } from "react";
import {
  Card, CardBody, Container, Row, Col, Label, Button, Table,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import toastr from "toastr";
import { downloadInvoicePDF } from "./InvoicePDF";

// ── Invoice number helpers ─────────────────────────────────────────────────────
function getLastUsedNo() {
  return parseInt(localStorage.getItem("lastInvoiceNo") || "0", 10);
}

function getNextInvoiceNo() {
  return getLastUsedNo() + 1;
}

// Use the given invoice number string and update the counter from its numeric part.
function consumeInvoiceNo(fieldValue) {
  const trimmed = (fieldValue || "").trim();
  // Extract trailing number (handles "G/11", "11", "INV-11", etc.)
  const match = trimmed.match(/(\d+)\s*$/);
  const num = match ? parseInt(match[1], 10) : getNextInvoiceNo();
  // Advance counter to at least the number we just used
  if (num >= getLastUsedNo()) {
    localStorage.setItem("lastInvoiceNo", String(num));
  }
  return trimmed || `G/${num}`;
}

// ── Save to billing history ───────────────────────────────────────────────────
function saveToHistory(key, invoiceNo, customerName, date, amount, invoiceData) {
  try {
    const raw = localStorage.getItem("invoiceHistory");
    const hist = raw ? JSON.parse(raw) : [];
    hist.unshift({ key, invoiceNo, customerName, date, amount, invoiceData });
    localStorage.setItem("invoiceHistory", JSON.stringify(hist));
    // Also keep the raw payload under the key for backward compat
    localStorage.setItem(key, JSON.stringify({ invoiceData }));
  } catch (e) {
    console.error("Failed to save invoice history", e);
  }
}

// ── Default empty item ────────────────────────────────────────────────────────
const emptyItem = () => ({
  description: "HIGH SPEED DIESEL BS VI LTR",
  hsn: "27101944",
  qty: "",
  rate: "",
});

// ── Component ─────────────────────────────────────────────────────────────────
const CreateBilling = () => {
  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "Create Billing", link: "#" },
  ];

  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [phone, setPhone] = useState("");
  // Pre-fill with next invoice number; user can edit freely
  const [invoiceNo, setInvoiceNo] = useState(() => `G/${getNextInvoiceNo()}`);
  const [items, setItems] = useState([emptyItem()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── Item helpers ──
  const updateItem = useCallback((idx, field, val) => {
    setItems(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: val };
      return copy;
    });
  }, []);

  const addItem = () => setItems(prev => [...prev, emptyItem()]);

  const removeItem = (idx) =>
    setItems(prev => prev.filter((_, i) => i !== idx));

  // ── Compute total ──
  const totalAmount = items.reduce(
    (s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0),
    0
  );

  // ── Submit ──
  const handleGenerate = () => {
    if (!customerName.trim()) {
      setError("Please enter customer name.");
      return;
    }
    const hasItem = items.some(
      (it) => it.qty && it.rate && parseFloat(it.qty) > 0 && parseFloat(it.rate) > 0
    );
    if (!hasItem) {
      setError("Please fill at least one item with qty and rate.");
      return;
    }
    setError("");
    setLoading(true);

    // consumeInvoiceNo updates localStorage counter and returns the final invoice number
    const finalInvoiceNo = consumeInvoiceNo(invoiceNo);
    const invoiceData = {
      invoiceNo: finalInvoiceNo,
      invoiceDate: date,
      buyerOrderNo: `S-${finalInvoiceNo}`,
      refNo: "",
      customerName: customerName.trim(),
      customerAddress: customerAddress.trim(),
      phone: phone.trim(),
      items: items
        .filter((it) => it.qty && it.rate)
        .map((it) => ({
          description: it.description || "HIGH SPEED DIESEL BS VI LTR",
          hsn: it.hsn || "27101944",
          qty: parseFloat(it.qty) || 0,
          rate: parseFloat(it.rate) || 0,
        })),
    };

    // Save to history immediately (before PDF generation)
    const key = `invoice_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    saveToHistory(key, finalInvoiceNo, customerName.trim(), date, totalAmount, invoiceData);

    // Fire PDF generation in background (iframe-based, may take 5–10 s)
    downloadInvoicePDF(invoiceData).catch((err) => {
      console.error("PDF generation failed:", err);
      toastr.error("PDF generation failed. Please try again from Billing History.");
    });

    // Advance displayed invoice number to the next one
    setInvoiceNo(`G/${getNextInvoiceNo()}`);

    toastr.success(`Invoice ${finalInvoiceNo} — PDF download will start shortly...`);
    setTimeout(() => setLoading(false), 1500);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Create Billing" breadcrumbItems={breadcrumbItems} />

        <Card>
          <CardBody>
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* ── Header fields ── */}
            <Row className="mb-3">
              <Col md={3}>
                <Label className="fw-semibold">Invoice No.</Label>
                <input
                  className="form-control"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                />
                <small className="text-muted">Auto-increments after each bill</small>
              </Col>
              <Col md={3}>
                <Label className="fw-semibold">Invoice Date</Label>
                <Flatpickr
                  className="form-control"
                  value={date}
                  options={{ dateFormat: "Y-m-d" }}
                  onChange={([d]) => {
                    if (d) {
                      const y = d.getFullYear();
                      const m = String(d.getMonth() + 1).padStart(2, "0");
                      const day = String(d.getDate()).padStart(2, "0");
                      setDate(`${y}-${m}-${day}`);
                    }
                  }}
                />
              </Col>
              <Col md={3}>
                <Label className="fw-semibold">Customer Name *</Label>
                <input
                  className="form-control"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="e.g. ZEE TEXTILE PVT LTD"
                />
              </Col>
              <Col md={3}>
                <Label className="fw-semibold">Phone</Label>
                <input
                  className="form-control"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Mobile / Landline"
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Label className="fw-semibold">Customer Address</Label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="Full address..."
                />
              </Col>
            </Row>

            {/* ── Items table ── */}
            <Label className="fw-semibold">Items</Label>
            <Table bordered size="sm" className="mb-2">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "35%" }}>Description</th>
                  <th style={{ width: "12%" }}>HSN/SAC</th>
                  <th style={{ width: "15%" }}>Qty (LTR)</th>
                  <th style={{ width: "15%" }}>Rate (Rs/LTR)</th>
                  <th style={{ width: "15%" }}>Amount</th>
                  <th style={{ width: "8%" }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const amt =
                    (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
                  return (
                    <tr key={idx}>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          value={item.description}
                          onChange={(e) =>
                            updateItem(idx, "description", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          value={item.hsn}
                          onChange={(e) =>
                            updateItem(idx, "hsn", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.qty}
                          onChange={(e) =>
                            updateItem(idx, "qty", e.target.value)
                          }
                          placeholder="0.00"
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.rate}
                          onChange={(e) =>
                            updateItem(idx, "rate", e.target.value)
                          }
                          placeholder="0.00"
                        />
                      </td>
                      <td className="text-end align-middle">
                        {amt > 0
                          ? amt.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : "-"}
                      </td>
                      <td className="text-center align-middle">
                        {items.length > 1 && (
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            onClick={() => removeItem(idx)}
                          >
                            ×
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} className="text-end fw-bold">
                    Total Amount
                  </td>
                  <td className="text-end fw-bold">
                    Rs.{" "}
                    {totalAmount.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </Table>

            <Row className="mb-4">
              <Col>
                <Button color="secondary" size="sm" outline onClick={addItem}>
                  + Add Item
                </Button>
              </Col>
            </Row>

            <Row>
              <Col>
                <Button
                  color="primary"
                  onClick={handleGenerate}
                  disabled={loading}
                >
                  {loading ? "Generating PDF..." : "Generate & Download PDF"}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default CreateBilling;
